import { campaignApi } from '@/api/feedback/campaignApi';
import { type Campaign, type CampaignSchedule, type Question, QuestionType } from '@/models/feedback/Campaign';
import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { DataGrid, type GridColDef, type GridRowParams } from '@mui/x-data-grid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useAppContext } from '../../AppContext';
import { useLocalizedStrings } from '../../localization/LocalizedStringsProvider';
import { usePermissions } from '../../permissions/usePermissions';
import { Perm } from '../../permissions/permissions';
import { fillHeight } from '../../theme';
import DataGridDeleteButton from '../common/data-grid/DataGridDeleteButton';
import DataGridEditButton from '../common/data-grid/DataGridEditButton';
import Spacer from '../common/Spacer';
import { useToast } from '../common/toast/ToastProvider';
import CampaignDialog from './dialogs/CampaignDialog';
import QuestionDialog from './dialogs/QuestionDialog';
import ScheduleDialog from './dialogs/ScheduleDialog';
import ScheduleCalendar from './ScheduleCalendar';
import { useAssignedLocations } from '../../permissions/useLocationScope';

// Height of the Campaigns and Questions grids. autoPageSize fits as many rows as this leaves room for, so
// the page size follows the height rather than the other way round - which is why ten rows is a number here
// and not a pageSize prop. 640 = a 56px header + a 52px footer + ten 52px rows, with enough slack that a
// pixel of rounding cannot cost a row.
const GRID_HEIGHT = 640;

const Campaigns = () => {
    const qc = useQueryClient();
    const toast = useToast();
    const strings = useLocalizedStrings();
    const app = useAppContext();
    const perms = usePermissions();
    const canCreate = perms.canCreate(Perm.feedback);
    const canUpdate = perms.canUpdate(Perm.feedback);
    const canDelete = perms.canDelete(Perm.feedback);

    // Scope comes from the global top-bar selector.
    const brandId = app.brandId ?? undefined;

    const [tab, setTab] = useState(0);
    const [campaignDialogOpen, setCampaignDialogOpen] = useState(false);
    const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);
    const [questionDialogOpen, setQuestionDialogOpen] = useState(false);
    const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
    const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
    const [editingSchedule, setEditingSchedule] = useState<CampaignSchedule | null>(null);
    const [scheduleCampaignId, setScheduleCampaignId] = useState<string | undefined>(undefined);

    const { data: campaigns, isPending: isCampaignsLoading } = useQuery<Campaign[]>({ queryKey: ['feedback-campaigns', brandId ?? ''], enabled: !!brandId, queryFn: () => campaignApi.listCampaigns(brandId!), staleTime: 0 });
    const { data: questions, isPending: isQuestionsLoading } = useQuery<Question[]>({ queryKey: ['feedback-questions', brandId ?? ''], enabled: !!brandId, queryFn: () => campaignApi.listQuestions(brandId!), staleTime: 0 });
    // Only the locations this user is assigned to - schedule targeting offered the brand's whole estate (TTD-160).
    const { locations } = useAssignedLocations(brandId);

    const { mutateAsync: deleteCampaign } = useMutation({
        mutationFn: (id: string) => campaignApi.deleteCampaign(id),
        onSuccess: async () => { toast({ severity: 'success', message: strings.feedbackCampaignDeleted }); await qc.invalidateQueries({ queryKey: ['feedback-campaigns', brandId ?? ''] }); },
        onError: () => toast({ severity: 'error', message: strings.feedbackCampaignDeleteError }),
    });

    const { mutateAsync: deleteQuestion } = useMutation({
        mutationFn: (id: string) => campaignApi.deleteQuestion(id),
        onSuccess: async () => { toast({ severity: 'success', message: strings.feedbackQuestionDeleted }); await qc.invalidateQueries({ queryKey: ['feedback-questions', brandId ?? ''] }); },
        onError: () => toast({ severity: 'error', message: strings.feedbackQuestionDeleteError }),
    });

    const openEditCampaign = (c: Campaign) => { setEditingCampaign(c); setCampaignDialogOpen(true); };

    const campaignColumns: GridColDef<Campaign>[] = [
        { field: 'name', headerName: strings.name, flex: 2 },
        { field: 'instruction', headerName: strings.feedbackInstruction, flex: 3 },
        { field: 'questionCount', headerName: strings.feedbackQuestions, flex: 1, valueGetter: (_value, row) => row.questionIds?.length ?? 0 },
        {
            field: 'actions', headerName: strings.actions, type: 'actions', flex: 1,
            getActions: (params: GridRowParams<Campaign>) => [
                <DataGridEditButton id={params.id.toString()} onClick={() => openEditCampaign(params.row)} aria-label="edit campaign" readOnly={!canUpdate} />,
                <DataGridDeleteButton id={params.id.toString()} getWarningMessage={(id) => strings.feedbackDeleteCampaignConfirm.replace('{{name}}', campaigns?.find(c => c.id === id)?.name ?? '')} onClick={(id) => deleteCampaign(id)} aria-label="delete campaign" hidden={!canDelete} />,
            ],
        },
    ];

    const questionColumns: GridColDef<Question>[] = [
        { field: 'name', headerName: strings.feedbackQuestion, flex: 3 },
        {
            field: 'questionType', headerName: strings.feedbackQuestionType, flex: 1,
            valueGetter: (_v, row: Question) =>
                row.questionType === QuestionType.FreeText
                    ? strings.feedbackQuestionTypeFreeText
                    : strings.feedbackQuestionTypeRatingScale,
        },
        {
            field: 'trigger', headerName: strings.feedbackTrigger, flex: 1,
            // A free-text question has no threshold, and showing its stored 0 would read as "alerts at 0".
            valueGetter: (_v, row: Question) => (row.questionType === QuestionType.FreeText ? '—' : row.trigger),
        },
        {
            field: 'actions', headerName: strings.actions, type: 'actions', flex: 1,
            getActions: (params: GridRowParams<Question>) => [
                <DataGridEditButton id={params.id.toString()} onClick={() => { setEditingQuestion(params.row); setQuestionDialogOpen(true); }} aria-label="edit question" readOnly={!canUpdate} />,
                <DataGridDeleteButton id={params.id.toString()} getWarningMessage={(id) => strings.feedbackDeleteQuestionConfirm.replace('{{name}}', questions?.find(q => q.id === id)?.name ?? '')} onClick={(id) => deleteQuestion(id)} aria-label="delete question" hidden={!canDelete} />,
            ],
        },
    ];

    if (!brandId) {
        return (
            <Box sx={{ ...fillHeight, p: 2 }}>
                <Typography variant="body2" color="text.secondary">{strings.feedbackSelectBrandTopBar}</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ ...fillHeight, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={tab} onChange={(_e, v) => setTab(v)}>
                    <Tab label={strings.campaigns} />
                    <Tab label={strings.feedbackSchedule} />
                </Tabs>
            </Box>

            <Box sx={{ flex: 1, minHeight: 0, minWidth: 0, p: 2 }}>
                {tab === 0 ? (
                    <Stack spacing={3} sx={{ height: '100%', overflow: 'auto' }}>
                        <Box>
                            <Stack direction="row" alignItems="center" justifyContent="space-between">
                                <Typography variant="h6">{strings.campaigns}</Typography>
                                {canCreate && <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditingCampaign(null); setCampaignDialogOpen(true); }} size="input">{strings.feedbackNewCampaign}</Button>}
                            </Stack>
                            <Spacer y={1} />
                            <Box sx={{ height: GRID_HEIGHT }}>
                                <DataGrid
                                    rows={campaigns ?? []}
                                    columns={campaignColumns}
                                    autoPageSize
                                    loading={isCampaignsLoading}
                                    localeText={{ noRowsLabel: strings.feedbackNoCampaigns }}
                                />
                            </Box>
                        </Box>

                        <Box>
                            <Stack direction="row" alignItems="center" justifyContent="space-between">
                                <Typography variant="h6">{strings.feedbackQuestions}</Typography>
                                {canCreate && <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditingQuestion(null); setQuestionDialogOpen(true); }} size="input">{strings.feedbackNewQuestion}</Button>}
                            </Stack>
                            <Spacer y={1} />
                            <Box sx={{ height: GRID_HEIGHT }}>
                                <DataGrid
                                    rows={questions ?? []}
                                    columns={questionColumns}
                                    autoPageSize
                                    loading={isQuestionsLoading}
                                    localeText={{ noRowsLabel: strings.feedbackNoQuestions }}
                                />
                            </Box>
                        </Box>
                    </Stack>
                ) : (
                    <Stack sx={{ height: '100%' }} spacing={1}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            {canCreate && <Button variant="contained" startIcon={<AddIcon />} size="input" onClick={() => { setEditingSchedule(null); setScheduleCampaignId(undefined); setScheduleDialogOpen(true); }}>{strings.feedbackNewSchedule}</Button>}
                        </Box>
                        <Spacer y={1} />
                        <Box sx={{ flex: 1, minHeight: 0 }}>
                            <ScheduleCalendar campaigns={campaigns ?? []} onSelectSchedule={(schedule, campaignId) => { setEditingSchedule(schedule); setScheduleCampaignId(campaignId); setScheduleDialogOpen(true); }} />
                        </Box>
                    </Stack>
                )}
            </Box>

            <CampaignDialog
                open={campaignDialogOpen}
                brandId={brandId}
                campaign={editingCampaign}
                readOnly={!!editingCampaign && !canUpdate}
                questions={questions ?? []}
                onClose={() => setCampaignDialogOpen(false)}
            />
            <QuestionDialog
                open={questionDialogOpen}
                brandId={brandId}
                question={editingQuestion}
                readOnly={!!editingQuestion && !canUpdate}
                onClose={() => setQuestionDialogOpen(false)}
            />
            <ScheduleDialog
                open={scheduleDialogOpen}
                campaignId={scheduleCampaignId}
                campaigns={campaigns ?? []}
                brandId={brandId}
                locations={locations ?? []}
                schedule={editingSchedule}
                readOnly={!!editingSchedule && !canUpdate}
                onClose={() => setScheduleDialogOpen(false)}
            />
        </Box>
    );
};

export default Campaigns;
